import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Smoothly scrolls to a URL hash on:
 *  - cross-page navigation (Link to "/#section" from /insights/abc)
 *  - same-page hash change (Link to "/#section" while already on /)
 *
 * React Router v6's useLocation does not always re-fire when only the hash
 * changes on the same pathname, so we also listen to the native
 * `hashchange` window event.
 */
function scrollToHash(hash) {
  if (!hash) return;
  const id = hash.startsWith("#") ? hash.slice(1) : hash;
  let attempts = 0;
  const tryScroll = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    attempts += 1;
    if (attempts < 20) setTimeout(tryScroll, 80);
  };
  tryScroll();
}

export default function ScrollToHash() {
  const { hash, pathname } = useLocation();

  // 1. Route-change driven scrolls
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
      return;
    }
    scrollToHash(hash);
  }, [hash, pathname]);

  // 2. Native hashchange (covers Link clicks that don't trigger useLocation)
  useEffect(() => {
    const onHashChange = () => scrollToHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // 3. Same-link clicks (Link to current hash) — capture clicks at document level
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      // Match "/#section" or "#section"
      if (!href.includes("#")) return;
      const hashIdx = href.indexOf("#");
      const targetHash = href.slice(hashIdx);
      const targetPath = href.slice(0, hashIdx) || "/";
      // Only intercept when navigating to the same path (so React Router still
      // handles cross-page links normally)
      const currentPath = window.location.pathname;
      if (targetPath !== currentPath && targetPath !== "" ) return;
      // Defer until the URL updates
      setTimeout(() => scrollToHash(targetHash), 30);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
